import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AgChartOptions } from 'ag-charts-community';
import { AgCharts } from 'ag-charts-angular';
import { getPaymentData } from '../../target-chart/targetData';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';

interface PaymentChartDatum {
  label: string;
  amount: number;
}

@Component({
  selector: 'app-training-wise-payment-chart',
  imports: [AgCharts, MatSelectModule, MatInputModule, MatFormFieldModule, MatIconModule, CommonModule, FormsModule],
  templateUrl: './training-wise-payment-chart.component.html',
  styleUrl: './training-wise-payment-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrainingWisePaymentChartComponent implements OnChanges {
  @Input() paymentData: any[] | null = null;

  options!: AgChartOptions;
  isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.isBrowser) {
      this.options = this.getBarOptions(this.normalizePaymentData(null));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paymentData'] && this.isBrowser) {
      this.options = this.getBarOptions(this.normalizePaymentData(this.paymentData));
    }
  }

  private getBarOptions(data: PaymentChartDatum[]): AgChartOptions {
    return {
      data,
      series: [
        {
          type: 'bar',
          direction: 'horizontal',
          xKey: 'label',
          yKey: 'amount',
          yName: 'Payment Disbursed (Rs)',
          fill: '#d16f32',
          stroke: '#d16f32',
          label: {
            enabled: true,
            formatter: ({ value }) => `Rs ${(value).toFixed(2)}`,
          },
          tooltip: {
            renderer: ({ datum }) => ({
              title: datum.label,
              content: `Disbursed: Rs ${datum.amount.toLocaleString('en-IN')}`,
            }),
          },
        },
      ],
      legend: {
        enabled: true,
        position: 'bottom',
      },
      axes: [
        {
          type: 'category',
          position: 'left',
        },
        {
          type: 'number',
          position: 'bottom',
        },
      ],
      background: {
        fill: 'rgba(0, 0, 0, 0)',
      },
    };
  }

  private normalizePaymentData(data: any[] | null | undefined): PaymentChartDatum[] {
    if (!Array.isArray(data) || data.length === 0) {
      return this.mapStaticData(getPaymentData());
    }

    const mappedData = data
      .map((item) => this.mapApiItem(item))
      .filter((item): item is PaymentChartDatum => item !== null);

    if (!mappedData.length) {
      return this.mapStaticData(getPaymentData());
    }

    const districtWiseTotals = new Map<string, number>();

    for (const item of mappedData) {
      districtWiseTotals.set(item.label, (districtWiseTotals.get(item.label) ?? 0) + item.amount);
    }

    return Array.from(districtWiseTotals.entries()).map(([label, amount]) => ({ label, amount }));
  }

  private mapStaticData(data: any[]): PaymentChartDatum[] {
    return data.map((item) => ({
      label: String(item?.district ?? item?.label ?? 'Unknown'),
      amount: this.toAmount(item?.disbursed ?? item?.amount) ?? 0,
    }));
  }

  private mapApiItem(item: any): PaymentChartDatum | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const districtName = this.pickFirstString(item, [
      'district',
      'districtName',
      'description',
      'name',
    ]);
    const label = districtName;

    const amount = this.pickFirstNumber(item, [
      'payAmount',
      'disbursed',
      'disbursedAmount',
      'paymentDisbursed',
      'amount',
      'amountDisbursed',
      'totalAmount',
      'paidAmount',
    ]);

    if (!label || amount === null) {
      return null;
    }

    return { label, amount };
  }

  private pickFirstString(item: Record<string, unknown>, keys: string[]): string | null {
    for (const key of keys) {
      const value = item[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    return null;
  }

  private pickFirstNumber(item: Record<string, unknown>, keys: string[]): number | null {
    for (const key of keys) {
      const value = this.toAmount(item[key]);
      if (value !== null) {
        return value;
      }
    }

    return null;
  }

  private toAmount(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const sanitizedValue = value.replace(/,/g, '').trim();
      const parsedValue = Number(sanitizedValue);
      return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    return null;
  }
}
